import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Typography,
  Card,
  CardContent,
  Button,
  Box,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  Skeleton,
  useTheme,
  keyframes,
  styled,
  Paper,
  Divider,
  Container
} from '@mui/material';
import {
  CheckCircle,
  Warning,
  Error as ErrorIcon,
  TrendingUp,
  Battery90,
  Upload,
  Refresh,
  GetApp,
  ContactMail,
  Speed,
  FlashOn,
  TrendingDown,
  Assessment
} from '@mui/icons-material';
import { useAnalysis, useChartData } from '../hooks/useApi';
import apiClient from '../services/api';
import TrafficLight from '../components/TrafficLight';
import PowerProfileChart from '../components/PowerProfileChart';
import CustomerForm, { CustomerFormData, CustomerFormSubmissionData } from '../components/CustomerForm';
import { leadService } from '../services/leadService';
import AdvancedMetricsPanel from '../components/AdvancedMetricsPanel';
import SupercapCalculationsPanel from '../components/SupercapCalculationsPanel';
import SupercapDesignPanel from '../components/SupercapDesignPanel';
import ZoomableCanvas from '../components/ZoomableCanvas';
import type { AnalysisMetrics, AnalysisScore } from '../types/api';

// Gradient animation for title
const gradient = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const AnimatedTitle = styled(Typography)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 50%, ${theme.palette.info.main} 100%)`,
  backgroundSize: '200% 200%',
  animation: `${gradient} 6s ease infinite`,
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  fontWeight: 800,
  letterSpacing: '-0.02em',
}));

// Glassmorphism Card Style
const GlassCard = styled(Card)(({ theme }) => ({
  background: theme.palette.mode === 'dark'
    ? 'rgba(255, 255, 255, 0.05)'
    : 'rgba(255, 255, 255, 0.9)',
  backdropFilter: 'blur(20px)',
  borderRadius: '24px',
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'}`,
  boxShadow: theme.palette.mode === 'dark'
    ? '0 8px 32px rgba(0, 0, 0, 0.3)'
    : '0 8px 32px rgba(0, 0, 0, 0.08)',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.palette.mode === 'dark'
      ? '0 12px 48px rgba(0, 0, 0, 0.4)'
      : '0 12px 48px rgba(0, 0, 0, 0.12)',
  },
}));

// Metric Card with Icon
const MetricCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: '16px',
  background: theme.palette.mode === 'dark'
    ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(168, 85, 247, 0.1) 100%)'
    : 'linear-gradient(135deg, rgba(99, 102, 241, 0.05) 0%, rgba(168, 85, 247, 0.05) 100%)',
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'}`,
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'scale(1.02)',
    boxShadow: theme.palette.mode === 'dark'
      ? '0 8px 24px rgba(99, 102, 241, 0.2)'
      : '0 8px 24px rgba(99, 102, 241, 0.15)',
  },
}));

// Enhanced Alert Box
const ResultAlert = styled(Alert)(({ theme }) => ({
  borderRadius: '20px',
  padding: theme.spacing(3, 4),
  backdropFilter: 'blur(10px)',
  border: '2px solid',
  fontWeight: 500,
}));

const ResultsPage: React.FC = () => {
  const theme = useTheme();
  const { uploadId } = useParams<{ uploadId: string }>();
  const [downloadingReport, setDownloadingReport] = useState(false);
  const [showCustomerForm, setShowCustomerForm] = useState(false);
  const [submittingLead, setSubmittingLead] = useState(false);
  const [summaryData, setSummaryData] = useState<any>(null);
  const [loadingSummary, setLoadingSummary] = useState(false);

  // Get analysis results from API
  const {
    analysisResult,
    analyzing,
    error: analysisError,
    getResults
  } = useAnalysis(uploadId);

  // Get chart data - but ONLY load when analysis is complete
  const analysisComplete = analysisResult?.status === 'completed';
  const {
    chartData,
    loadingCharts,
    chartError,
    refreshCharts
  } = useChartData(analysisComplete ? uploadId : undefined);

  // Load results on component mount
  useEffect(() => {
    if (uploadId && !analysisResult) {
      getResults();
    }
  }, [uploadId, analysisResult, getResults]);

  // Load summary data with supercap design parameters
  useEffect(() => {
    const loadSummary = async () => {
      if (!uploadId || !analysisComplete) return;

      setLoadingSummary(true);
      try {
        const summary = await apiClient.getSummary(uploadId);
        setSummaryData(summary);
      } catch (error) {
        console.error('Failed to load summary:', error);
      } finally {
        setLoadingSummary(false);
      }
    };

    loadSummary();
  }, [uploadId, analysisComplete]);

  // Poll for updates if analysis is still processing
  useEffect(() => {
    let pollInterval: number;

    if (analysisResult?.status === 'processing' ||
        (analysisResult?.results?.score?.level === 'PROCESSING')) {
      pollInterval = window.setInterval(() => {
        getResults();
      }, 3000);
    }

    return () => {
      if (pollInterval) {
        window.clearInterval(pollInterval);
      }
    };
  }, [analysisResult?.status, analysisResult?.results?.score?.level, getResults]);

  // Download report function
  const handleDownloadReport = async () => {
    if (!uploadId) return;

    setDownloadingReport(true);
    try {
      const reportBlob = await apiClient.getReport(uploadId, 'pdf');

      const url = window.URL.createObjectURL(reportBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `storage-analysis-${uploadId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Report download failed:', error);
    } finally {
      setDownloadingReport(false);
    }
  };

  // Handle customer form submission
  const handleCustomerFormSubmit = async (formData: CustomerFormSubmissionData) => {
    setSubmittingLead(true);
    try {
      const response = await leadService.submitLead(formData);

      if (response.success) {
        alert('Vielen Dank für Ihre Anfrage! Wir werden uns zeitnah bei Ihnen melden.');
        setShowCustomerForm(false);
      } else {
        throw new Error(response.message || 'Unbekannter Fehler');
      }
    } catch (error) {
      console.error('Lead submission failed:', error);
      alert(error instanceof Error ? error.message : 'Fehler beim Senden der Anfrage');
    } finally {
      setSubmittingLead(false);
    }
  };

  const getRecommendationConfig = (recommendation: string | number) => {
    switch (recommendation) {
      case 'green':
      case 'grün':
      case 3:
        return {
          color: 'success',
          icon: <CheckCircle sx={{ fontSize: 40 }} />,
          title: 'Optimal geeignet',
          description: 'Hybrid-Speicher sind für Ihr Lastprofil perfekt abgestimmt!',
          gradient: 'linear-gradient(135deg, #4CAF50 0%, #81C784 100%)'
        };
      case 'yellow':
      case 'gelb':
      case 2:
        return {
          color: 'warning',
          icon: <Warning sx={{ fontSize: 40 }} />,
          title: 'Prüfenswert',
          description: 'Hybrid-Speicher könnten für Ihr Lastprofil eine sinnvolle Investition sein.',
          gradient: 'linear-gradient(135deg, #FF9800 0%, #FFB74D 100%)'
        };
      case 'red':
      case 'rot':
      case 1:
        return {
          color: 'error',
          icon: <ErrorIcon sx={{ fontSize: 40 }} />,
          title: 'Eingeschränkt geeignet',
          description: 'Alternative Speicherlösungen könnten für Ihr Profil besser passen.',
          gradient: 'linear-gradient(135deg, #f44336 0%, #e57373 100%)'
        };
      case 'processing':
        return {
          color: 'info',
          icon: <CircularProgress size={40} />,
          title: 'Analyse läuft',
          description: 'Die Bewertung wird gerade berechnet...',
          gradient: 'linear-gradient(135deg, #2196F3 0%, #64B5F6 100%)'
        };
      default:
        return {
          color: 'info',
          icon: <TrendingUp sx={{ fontSize: 40 }} />,
          title: 'Unbekannt',
          description: 'Bewertung konnte nicht ermittelt werden.',
          gradient: 'linear-gradient(135deg, #9E9E9E 0%, #BDBDBD 100%)'
        };
    }
  };

  // Loading state
  if (analyzing || (!analysisResult && !analysisError)) {
    return (
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <AnimatedTitle variant="h2" gutterBottom>
            Analyseergebnis
          </AnimatedTitle>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
            Ihre Daten werden analysiert...
          </Typography>
        </Box>

        <Box display="flex" flexDirection="column" alignItems="center" gap={4}>
          <CircularProgress size={60} thickness={4} />
          <Typography variant="body1" color="text.secondary">
            Analyse wird verarbeitet...
          </Typography>
          <Skeleton variant="rectangular" width="100%" height={300} sx={{ borderRadius: '24px' }} />
        </Box>
      </Container>
    );
  }

  // Error state
  if (analysisError) {
    return (
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <AnimatedTitle variant="h2" gutterBottom>
            Analyseergebnis
          </AnimatedTitle>
        </Box>

        <ResultAlert severity="error" icon={<ErrorIcon />}>
          <Typography variant="h6" gutterBottom>Fehler beim Laden</Typography>
          <Typography variant="body1">
            {analysisError.message}
          </Typography>
        </ResultAlert>

        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Button
            variant="contained"
            size="large"
            onClick={getResults}
            startIcon={<Refresh />}
            sx={{ borderRadius: '12px', px: 4, py: 1.5 }}
          >
            Erneut versuchen
          </Button>
        </Box>
      </Container>
    );
  }

  // Extract data from analysis result
  const metrics = (analysisResult?.results?.metrics || analysisResult?.metrics || {}) as AnalysisMetrics;
  const score = (analysisResult?.results?.score || analysisResult?.score || {}) as AnalysisScore;
  const recommendation = (score.level === 'PROCESSING' ? 'processing' : (score.color || 'unknown')) as 'green' | 'yellow' | 'red' | 'grün' | 'gelb' | 'rot' | 1 | 2 | 3 | 'processing' | 'unknown';
  const recommendationConfig = getRecommendationConfig(recommendation);

  // For TrafficLight component, convert processing/unknown to valid status
  const trafficLightStatus = (recommendation === 'processing' || recommendation === 'unknown' ? 'yellow' : recommendation) as 'green' | 'yellow' | 'red' | 'grün' | 'gelb' | 'rot' | 1 | 2 | 3;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: theme.palette.mode === 'dark'
          ? 'linear-gradient(180deg, rgba(13, 27, 42, 0.8) 0%, rgba(27, 38, 59, 0.8) 100%)'
          : 'linear-gradient(180deg, rgba(237, 242, 247, 0.8) 0%, rgba(247, 250, 252, 0.8) 100%)',
        py: 6
      }}
    >
      <Container maxWidth="lg">
        {/* Hero Section */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <AnimatedTitle variant="h2" gutterBottom sx={{ mb: 2 }}>
            Analyseergebnis
          </AnimatedTitle>

          <Typography
            variant="h6"
            color="text.secondary"
            sx={{
              maxWidth: '800px',
              mx: 'auto',
              lineHeight: 1.8,
              fontWeight: 400,
              opacity: 0.9
            }}
          >
            Upload-ID: {uploadId} • Analysiert am {new Date(analysisResult?.created_at || Date.now()).toLocaleDateString('de-DE', {
              day: '2-digit',
              month: 'long',
              year: 'numeric'
            })}
          </Typography>
        </Box>

        {/* Traffic Light & Main Result */}
        <Grid container spacing={4} sx={{ mb: 6 }}>
          <Grid item xs={12} md={8}>
            <GlassCard
              sx={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                background: `${recommendationConfig.gradient} !important`,
                borderRadius: '28px',
                overflow: 'hidden',
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: theme.palette.mode === 'dark'
                    ? 'rgba(0, 0, 0, 0.2)'
                    : 'rgba(255, 255, 255, 0.1)',
                  pointerEvents: 'none'
                }
              }}
            >
              <CardContent sx={{ position: 'relative', zIndex: 1, p: 5, width: '100%' }}>
                <Box display="flex" alignItems="center" gap={3} mb={3}>
                  {recommendationConfig.icon}
                  <Typography variant="h3" fontWeight="800" sx={{ color: 'white' }}>
                    {recommendationConfig.title}
                  </Typography>
                </Box>

                <Typography variant="h6" sx={{ color: 'rgba(255, 255, 255, 0.95)', lineHeight: 1.7, mb: 3 }}>
                  {recommendationConfig.description}
                </Typography>

                {score.score_value !== undefined && score.score_value !== null && score.score_value > 0 && (
                  <Chip
                    label={`Bewertung: ${score.score_value}/100 Punkten`}
                    sx={{
                      bgcolor: 'rgba(255, 255, 255, 0.25)',
                      backdropFilter: 'blur(10px)',
                      color: 'white',
                      fontWeight: 800,
                      fontSize: '1.1rem',
                      px: 3,
                      py: 3.5,
                      border: '2px solid rgba(255, 255, 255, 0.3)',
                      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)'
                    }}
                  />
                )}
              </CardContent>
            </GlassCard>
          </Grid>

          <Grid item xs={12} md={4} display="flex" justifyContent="center" alignItems="center">
            <GlassCard
              sx={{
                p: 5,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '100%',
                background: theme.palette.mode === 'dark'
                  ? 'rgba(30, 30, 40, 0.6)'
                  : 'rgba(255, 255, 255, 0.8)'
              }}
            >
              <TrafficLight
                status={trafficLightStatus}
                score={score.score_value}
                recommendation={score.recommendation}
                size="large"
              />
            </GlassCard>
          </Grid>
        </Grid>

        {/* Power Profile Chart - MOVED TO END for Progressive Loading */}

        {/* Advanced Metrics Panel (Meeting 2025-10-03) */}
        {analysisComplete && uploadId && (
          <Box sx={{ mb: 6 }}>
            <Typography
              variant="h4"
              fontWeight="800"
              gutterBottom
              sx={{
                mb: 4,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              Erweiterte Analysen
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              Detaillierte Frequenzanalysen, Peak-Energy-Berechnungen und Lade-/Entlade-Effizienz
            </Typography>
            <AdvancedMetricsPanel uploadId={uploadId} />
          </Box>
        )}

        {/* Supercap Design Parameters (IECON2023) */}
        {analysisComplete && summaryData?.supercap_design && (
          <Box sx={{ mb: 6 }}>
            <SupercapDesignPanel supercapDesign={summaryData.supercap_design} />
          </Box>
        )}

        {/* Super-Capacitor Calculations Panel (IECON2023 Paper) */}
        {analysisComplete && uploadId && (
          <Box sx={{ mb: 6 }}>
            <Typography
              variant="h4"
              fontWeight="800"
              gutterBottom
              sx={{
                mb: 4,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              Super-Kondensator Modul-Auslegung
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              Wissenschaftliche Berechnungen basierend auf IECON2023
            </Typography>
            <SupercapCalculationsPanel uploadId={uploadId} />
          </Box>
        )}

        {/* Power Profile Chart - LOADS LAST for Progressive Loading */}
        {loadingCharts && (
          <GlassCard sx={{ mb: 6, textAlign: 'center', py: 8 }}>
            <CircularProgress size={60} thickness={4.5} />
            <Typography variant="h6" color="text.secondary" sx={{ mt: 4, fontWeight: 600 }}>
              Leistungsverlauf wird geladen...
            </Typography>
          </GlassCard>
        )}

        {!loadingCharts && chartData?.charts?.power_profile?.data && (
          <GlassCard sx={{ mb: 6, overflow: 'hidden' }}>
            <CardContent sx={{ p: 5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '16px',
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2.5,
                    boxShadow: `0 8px 24px ${theme.palette.primary.main}40`
                  }}
                >
                  <Assessment sx={{ fontSize: 32, color: 'white' }} />
                </Box>
                <Typography variant="h4" fontWeight="800">
                  Leistungsverlauf
                </Typography>
              </Box>
              <Divider sx={{ mb: 5, opacity: 0.3 }} />
              <Box sx={{ width: '100%', minHeight: 450 }}>
                <ZoomableCanvas title="Leistungsverlauf">
                  <PowerProfileChart
                    chartData={chartData.charts.power_profile.data}
                    title=""
                    height={450}
                  />
                </ZoomableCanvas>
              </Box>
            </CardContent>
          </GlassCard>
        )}

        {/* Metrics Grid */}
        <Box sx={{ mb: 6 }}>
          <Typography
            variant="h4"
            fontWeight="800"
            gutterBottom
            sx={{
              mb: 4,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            Analysierte Kennzahlen
          </Typography>

        <Grid container spacing={3}>

          <Grid item xs={12} sm={6} md={4}>
            <MetricCard elevation={0}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <FlashOn sx={{ fontSize: 32, color: theme.palette.error.main, mr: 1.5 }} />
                <Typography variant="subtitle2" color="text.secondary" fontWeight="600">
                  Spitzenlast
                </Typography>
              </Box>
              <Typography variant="h3" fontWeight="800" color="primary">
                {metrics.peak_power_kw !== undefined && metrics.peak_power_kw !== null
                  ? (metrics.peak_power_kw * 1000).toFixed(0)
                  : 'N/A'}
              </Typography>
              <Typography variant="h6" color="text.secondary">
                Watt
              </Typography>
            </MetricCard>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <MetricCard elevation={0}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Speed sx={{ fontSize: 32, color: theme.palette.info.main, mr: 1.5 }} />
                <Typography variant="subtitle2" color="text.secondary" fontWeight="600">
                  Durchschnittslast
                </Typography>
              </Box>
              <Typography variant="h3" fontWeight="800" color="primary">
                {metrics.average_power_kw !== undefined && metrics.average_power_kw !== null
                  ? (metrics.average_power_kw * 1000).toFixed(0)
                  : 'N/A'}
              </Typography>
              <Typography variant="h6" color="text.secondary">
                Watt
              </Typography>
            </MetricCard>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <MetricCard elevation={0}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TrendingUp sx={{ fontSize: 32, color: theme.palette.success.main, mr: 1.5 }} />
                <Typography variant="subtitle2" color="text.secondary" fontWeight="600">
                  Peak-Ratio
                </Typography>
              </Box>
              <Typography variant="h3" fontWeight="800" color="primary">
                {metrics.peak_to_average_ratio !== undefined && metrics.peak_to_average_ratio !== null
                  ? metrics.peak_to_average_ratio.toFixed(1)
                  : 'N/A'}
              </Typography>
              <Typography variant="h6" color="text.secondary">
                x Mittelwert
              </Typography>
            </MetricCard>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <MetricCard elevation={0}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TrendingDown sx={{ fontSize: 32, color: theme.palette.warning.main, mr: 1.5 }} />
                <Typography variant="subtitle2" color="text.secondary" fontWeight="600">
                  Burst-Anteil
                </Typography>
              </Box>
              <Typography variant="h3" fontWeight="800" color="primary">
                {metrics.burst_share_percent !== undefined && metrics.burst_share_percent !== null
                  ? metrics.burst_share_percent.toFixed(1)
                  : 'N/A'}
              </Typography>
              <Typography variant="h6" color="text.secondary">
                Prozent
              </Typography>
            </MetricCard>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <MetricCard elevation={0}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Battery90 sx={{ fontSize: 32, color: theme.palette.secondary.main, mr: 1.5 }} />
                <Typography variant="subtitle2" color="text.secondary" fontWeight="600">
                  Energieverbrauch
                </Typography>
              </Box>
              <Typography variant="h3" fontWeight="800" color="primary">
                {metrics.total_energy_kwh !== undefined && metrics.total_energy_kwh !== null
                  ? (metrics.total_energy_kwh * 1000).toFixed(2)
                  : 'N/A'}
              </Typography>
              <Typography variant="h6" color="text.secondary">
                Wh
              </Typography>
            </MetricCard>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <MetricCard elevation={0}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TrendingUp sx={{ fontSize: 32, color: theme.palette.primary.main, mr: 1.5 }} />
                <Typography variant="subtitle2" color="text.secondary" fontWeight="600">
                  Lastfaktor
                </Typography>
              </Box>
              <Typography variant="h3" fontWeight="800" color="primary">
                {metrics.load_factor !== undefined && metrics.load_factor !== null
                  ? metrics.load_factor.toFixed(2)
                  : 'N/A'}
              </Typography>
              <Typography variant="h6" color="text.secondary">
                Effizienz
              </Typography>
            </MetricCard>
          </Grid>

          {/* Recuperation Metrics - nur anzeigen wenn vorhanden */}
          {metrics.recuperation_count && metrics.recuperation_count > 0 && (
            <>
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }}>
                  <Chip 
                    label="Energie-Rückgewinnung (Rekuperation)" 
                    color="success" 
                    icon={<Battery90 />}
                    sx={{ fontWeight: 600 }}
                  />
                </Divider>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <MetricCard elevation={0} sx={{ 
                  borderLeft: `4px solid ${theme.palette.success.main}`,
                  background: theme.palette.mode === 'dark'
                    ? 'linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(22, 163, 74, 0.1) 100%)'
                    : 'linear-gradient(135deg, rgba(34, 197, 94, 0.05) 0%, rgba(22, 163, 74, 0.05) 100%)',
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Battery90 sx={{ fontSize: 32, color: theme.palette.success.main, mr: 1.5 }} />
                    <Typography variant="subtitle2" color="text.secondary" fontWeight="600">
                      Zurückgewonnene Energie
                    </Typography>
                  </Box>
                  <Typography variant="h3" fontWeight="800" color="success.main">
                    {metrics.recuperation_energy_kwh !== undefined && metrics.recuperation_energy_kwh !== null
                      ? (metrics.recuperation_energy_kwh * 1000).toFixed(0)
                      : '0'}
                  </Typography>
                  <Typography variant="h6" color="text.secondary">
                    Wh
                  </Typography>
                </MetricCard>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <MetricCard elevation={0} sx={{ 
                  borderLeft: `4px solid ${theme.palette.success.main}`,
                  background: theme.palette.mode === 'dark'
                    ? 'linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(22, 163, 74, 0.1) 100%)'
                    : 'linear-gradient(135deg, rgba(34, 197, 94, 0.05) 0%, rgba(22, 163, 74, 0.05) 100%)',
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <FlashOn sx={{ fontSize: 32, color: theme.palette.success.main, mr: 1.5 }} />
                    <Typography variant="subtitle2" color="text.secondary" fontWeight="600">
                      Max. Rekuperations-Leistung
                    </Typography>
                  </Box>
                  <Typography variant="h3" fontWeight="800" color="success.main">
                    {metrics.recuperation_peak_kw !== undefined && metrics.recuperation_peak_kw !== null
                      ? (metrics.recuperation_peak_kw * 1000).toFixed(0)
                      : '0'}
                  </Typography>
                  <Typography variant="h6" color="text.secondary">
                    Watt
                  </Typography>
                </MetricCard>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <MetricCard elevation={0} sx={{ 
                  borderLeft: `4px solid ${theme.palette.success.main}`,
                  background: theme.palette.mode === 'dark'
                    ? 'linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(22, 163, 74, 0.1) 100%)'
                    : 'linear-gradient(135deg, rgba(34, 197, 94, 0.05) 0%, rgba(22, 163, 74, 0.05) 100%)',
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <TrendingDown sx={{ fontSize: 32, color: theme.palette.success.main, mr: 1.5 }} />
                    <Typography variant="subtitle2" color="text.secondary" fontWeight="600">
                      Rekuperations-Anteil
                    </Typography>
                  </Box>
                  <Typography variant="h3" fontWeight="800" color="success.main">
                    {metrics.recuperation_percentage !== undefined && metrics.recuperation_percentage !== null
                      ? metrics.recuperation_percentage.toFixed(1)
                      : 'N/A'}
                  </Typography>
                  <Typography variant="h6" color="text.secondary">
                    Prozent
                  </Typography>
                </MetricCard>
              </Grid>

              <Grid item xs={12}>
                <Alert severity="success" sx={{ borderRadius: 2 }}>
                  <Typography variant="subtitle2" fontWeight="600" gutterBottom>
                    Rekuperation erkannt
                  </Typography>
                  <Typography variant="body2">
                    Das System hat {metrics.recuperation_count || 0} Rekuperations-Events erkannt ({(metrics.recuperation_percentage || 0).toFixed(1)}% der Messungen).
                    Durch Energie-Rückgewinnung wurden {((metrics.recuperation_energy_kwh || 0) * 1000).toFixed(0)} Wh zurückgewonnen.
                    Netto-Energieverbrauch: {((metrics.net_energy_kwh || 0) * 1000).toFixed(0)} Wh.
                  </Typography>
                </Alert>
              </Grid>
            </>
          )}
        </Grid>
        </Box>

        {/* Criteria & Next Steps */}
        <Grid container spacing={4} sx={{ mb: 6 }}>
          <Grid item xs={12} md={6}>
            <GlassCard sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ p: 4, flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography variant="h5" fontWeight="700" gutterBottom display="flex" alignItems="center">
                  <CheckCircle sx={{ mr: 2, color: theme.palette.success.main }} />
                  Bewertungskriterien
                </Typography>

                <Divider sx={{ my: 3 }} />

                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight="700" color="success.main" gutterBottom>
                    🟢 Grün: Optimal für Hybrid-Speicher
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    • Peak-Ratio ≤ 5x Mittelwert<br />
                    • Burst-Anteil ≤ 20%
                  </Typography>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight="700" color="warning.main" gutterBottom>
                    🟡 Gelb: Prüfenswert
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    • Peak-Ratio &gt; 5x Mittelwert<br />
                    • Burst-Anteil &gt; 20%
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle1" fontWeight="700" color="error.main" gutterBottom>
                    🔴 Rot: Eingeschränkt geeignet
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    • Werte außerhalb der optimalen Bereiche
                  </Typography>
                </Box>
              </CardContent>
            </GlassCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <GlassCard sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ p: 4, flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography variant="h5" fontWeight="700" gutterBottom>
                  Nächste Schritte
                </Typography>

                <Divider sx={{ my: 3 }} />

                {(recommendation === 'green' || recommendation === 'grün') && (
                  <Typography paragraph sx={{ fontSize: '1.05rem', lineHeight: 1.7 }}>
                    🎉 Glückwunsch! Ihr Lastprofil eignet sich ausgezeichnet für Hybrid-Speichersysteme.
                    Kontaktieren Sie uns für eine detaillierte Wirtschaftlichkeitsanalyse.
                  </Typography>
                )}

                {(recommendation === 'yellow' || recommendation === 'gelb') && (
                  <Typography paragraph sx={{ fontSize: '1.05rem', lineHeight: 1.7 }}>
                    💡 Ihr Lastprofil zeigt Potenzial für Hybrid-Speichersysteme.
                    Lassen Sie sich von unseren Experten beraten.
                  </Typography>
                )}

                {(recommendation === 'red' || recommendation === 'rot') && (
                  <Typography paragraph sx={{ fontSize: '1.05rem', lineHeight: 1.7 }}>
                    🔍 Kontaktieren Sie uns dennoch - wir finden gemeinsam die beste Lösung für Ihre Anforderungen.
                  </Typography>
                )}

                <Box sx={{ mt: 'auto', pt: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => setShowCustomerForm(true)}
                    startIcon={<ContactMail />}
                    fullWidth
                    sx={{
                      borderRadius: '12px',
                      py: 1.5,
                      fontSize: '1rem',
                      fontWeight: 600,
                      background: recommendationConfig.gradient
                    }}
                  >
                    Jetzt Beratung anfragen
                  </Button>

                  <Button
                    component={Link}
                    to="/upload"
                    variant="outlined"
                    size="large"
                    startIcon={<Upload />}
                    fullWidth
                    sx={{ borderRadius: '12px', py: 1.5 }}
                  >
                    Neue Analyse starten
                  </Button>

                  <Button
                    variant="outlined"
                    size="large"
                    onClick={handleDownloadReport}
                    disabled={downloadingReport}
                    startIcon={downloadingReport ? <CircularProgress size={20} /> : <GetApp />}
                    fullWidth
                    sx={{ borderRadius: '12px', py: 1.5 }}
                  >
                    {downloadingReport ? 'Download läuft...' : 'Report herunterladen'}
                  </Button>
                </Box>
              </CardContent>
            </GlassCard>
          </Grid>
        </Grid>

        {/* Modern Features Section */}
        <Box sx={{ mt: 8, mb: 6 }}>
          <Typography
            variant="h4"
            fontWeight="800"
            gutterBottom
            sx={{
              mb: 1,
              textAlign: 'center',
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            Weitere Vorteile entdecken
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ textAlign: 'center', mb: 6, maxWidth: '800px', mx: 'auto' }}
          >
            Erfahren Sie mehr über unsere innovativen Funktionen und Analysemöglichkeiten
          </Typography>
        </Box>
      </Container>

      {/* Feature Cards Outside Container for Full Width */}
      <Box sx={{ mb: 6 }}>
        <Grid container spacing={3} sx={{ px: { xs: 2, md: 4 }, maxWidth: '1400px', mx: 'auto' }}>
          <Grid item xs={12} sm={6} lg={3}>
            <GlassCard sx={{ p: 4, height: '100%', textAlign: 'center' }}>
              <Box
                sx={{
                  width: 72,
                  height: 72,
                  borderRadius: '18px',
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}20 0%, ${theme.palette.primary.main}10 100%)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 3
                }}
              >
                <FlashOn sx={{ fontSize: 36, color: theme.palette.primary.main }} />
              </Box>
              <Typography variant="h6" fontWeight="700" gutterBottom>
                Schnelle Analyse
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                Automatisierte Bewertung in 1 - 3 Minuten
              </Typography>
            </GlassCard>
          </Grid>

          <Grid item xs={12} sm={6} lg={3}>
            <GlassCard sx={{ p: 4, height: '100%', textAlign: 'center' }}>
              <Box
                sx={{
                  width: 72,
                  height: 72,
                  borderRadius: '18px',
                  background: `linear-gradient(135deg, ${theme.palette.success.main}20 0%, ${theme.palette.success.main}10 100%)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 3
                }}
              >
                <TrendingUp sx={{ fontSize: 36, color: theme.palette.success.main }} />
              </Box>
              <Typography variant="h6" fontWeight="700" gutterBottom>
                Datenbasiert
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                Systematische Empfehlungen basierend auf Ihrem Profil
              </Typography>
            </GlassCard>
          </Grid>

          <Grid item xs={12} sm={6} lg={3}>
            <GlassCard sx={{ p: 4, height: '100%', textAlign: 'center' }}>
              <Box
                sx={{
                  width: 72,
                  height: 72,
                  borderRadius: '18px',
                  background: `linear-gradient(135deg, ${theme.palette.info.main}20 0%, ${theme.palette.info.main}10 100%)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 3
                }}
              >
                <Assessment sx={{ fontSize: 36, color: theme.palette.info.main }} />
              </Box>
              <Typography variant="h6" fontWeight="700" gutterBottom>
                Detaillierte Charts
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                Visualisierung Ihres Leistungsprofils
              </Typography>
            </GlassCard>
          </Grid>

          <Grid item xs={12} sm={6} lg={3}>
            <GlassCard sx={{ p: 4, height: '100%', textAlign: 'center' }}>
              <Box
                sx={{
                  width: 72,
                  height: 72,
                  borderRadius: '18px',
                  background: `linear-gradient(135deg, ${theme.palette.secondary.main}20 0%, ${theme.palette.secondary.main}10 100%)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 3
                }}
              >
                <CheckCircle sx={{ fontSize: 36, color: theme.palette.secondary.main }} />
              </Box>
              <Typography variant="h6" fontWeight="700" gutterBottom>
                Expertenberatung
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                Individuelle Beratung durch unsere Spezialisten
              </Typography>
            </GlassCard>
          </Grid>
        </Grid>
      </Box>

      {/* Customer Form Modal */}
      <CustomerForm
        open={showCustomerForm}
        onClose={() => setShowCustomerForm(false)}
        onSubmit={handleCustomerFormSubmit}
        uploadId={uploadId || ''}
        analysisResults={analysisResult}
        submitting={submittingLead}
      />
    </Box>
  );
};

export default ResultsPage;
